import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import lngs from "../../lngs";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LayersIcon from "@mui/icons-material/Layers";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import BusinessIcon from "@mui/icons-material/Business";
import ConstructionIcon from "@mui/icons-material/Construction";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import GavelIcon from "@mui/icons-material/Gavel";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentsIcon from "@mui/icons-material/Payments";
import MailIcon from "@mui/icons-material/Mail";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import TableChartIcon from "@mui/icons-material/TableChart";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import AddIcon from "@mui/icons-material/Add";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CategoryIcon from "@mui/icons-material/Category";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningIcon from "@mui/icons-material/Warning";
import SearchIcon from "@mui/icons-material/Search";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { Badge, Tooltip, Avatar, Menu, MenuItem, Divider, ListItemIcon, IconButton } from "@mui/material";
import { Bell, Settings, HelpCircle, User, LogOut, MessageCircle, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { fetchUnreadCount } from "../../actions/messagingActions";
import { useModules } from "../../hooks/useModules";
const TopNav = () => {
  const {
    t,
    i18n
  } = useTranslation("operations");
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentLang = i18n.resolvedLanguage;
  const isLanguageRTL = lngs[currentLang]?.isRtl || false;
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("profile")));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);
  const modules = useModules();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(document.fullscreenElement !== null);
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem("theme") === "dark");
  const unreadMessageCount = useSelector(state => state.messaging?.totalUnreadCount || 0);
  const profileOpen = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const languageOpen = Boolean(languageAnchorEl);
  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location]);
  useEffect(() => {
    const handleClickOutside = e => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    document.body.className = savedTheme;
  }, []);
  useEffect(() => {
    if (user) {
      dispatch(fetchUnreadCount());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const isRouteActive = path => location.pathname.toLowerCase().includes(path.toLowerCase());
  const isApp = location.pathname.toLowerCase().includes("/app/");
  const isModernPOSTerminal = location.pathname.toLowerCase().includes("/app/pos/terminal/");
  if (!user || !isApp || isModernPOSTerminal) return null;
  const openLink = link => history.push(link);
  const logout = () => {
    dispatch({
      type: "LOGOUT"
    });
    localStorage.removeItem("profile");
    history.push(`/${currentLang}/login`);
  };
  function toggleFullScreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }
  function toggleDarkMode() {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("theme", next ? t("dark") : t("light"));
      document.body.className = next ? t("dark") : t("light");
      return next;
    });
  }
  const handleLanguageChange = lng => {
    i18n.changeLanguage(lng);
    const newPath = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, `/${lng}`);
    history.replace(newPath);
    setLanguageAnchorEl(null);
  };
  const allMenuGroups = [{
    key: "accounting",
    label: t("accounting"),
    icon: <AccountBalanceIcon fontSize="small" />,
    items: [{
      label: t("accounting_home") || "Accounting Home",
      to: `/${currentLang}/app/accounting`,
      icon: <AccountBalanceIcon fontSize="small" />,
      active: isRouteActive("/app/accounting") && !isRouteActive("/app/accounting-reports"),
      moduleKey: "accounting"
    }, {
      label: t("invoices"),
      to: `/${currentLang}/app/invoices`,
      icon: <ReceiptIcon fontSize="small" />,
      active: isRouteActive("/app/invoices"),
      moduleKey: "accounting"
    }, {
      label: t("new_invoice"),
      to: `/${currentLang}/app/add/invoice`,
      icon: <AddIcon fontSize="small" />,
      active: isRouteActive("/app/add/invoice"),
      moduleKey: "accounting"
    }, {
      label: t("payments"),
      to: `/${currentLang}/app/payments`,
      icon: <PaymentsIcon fontSize="small" />,
      active: isRouteActive("/app/payments"),
      moduleKey: "accounting"
    }, {
      label: t("new_transaction"),
      to: `/${currentLang}/app/transaction`,
      icon: <AddIcon fontSize="small" />,
      active: isRouteActive("/app/transaction") && !isRouteActive("/app/transactions"),
      moduleKey: "accounting"
    }, {
      label: t("all_transaction"),
      to: `/${currentLang}/app/transactions`,
      icon: <SyncAltIcon fontSize="small" />,
      active: isRouteActive("/app/transactions"),
      moduleKey: "accounting"
    }, {
      label: t("pos"),
      to: `/${currentLang}/app/POS`,
      icon: <StorefrontIcon fontSize="small" />,
      active: isRouteActive("/app/POS"),
      moduleKey: "pos"
    }, {
      label: t("new_purchase_invoice"),
      to: `/${currentLang}/app/purchases/add`,
      icon: <ShoppingCartIcon fontSize="small" />,
      active: isRouteActive("/app/purchases/add"),
      moduleKey: "purchases"
    }, {
      label: t("purchase_invoices"),
      to: `/${currentLang}/app/purchases`,
      icon: <ReceiptIcon fontSize="small" />,
      active: isRouteActive("/app/purchases") && !isRouteActive("/app/purchases/add"),
      moduleKey: "purchases"
    }, {
      label: t("charts_of_accounts"),
      to: `/${currentLang}/app/accounts`,
      icon: <AccountBalanceIcon fontSize="small" />,
      active: isRouteActive("/app/accounts"),
      moduleKey: "accounting"
    }, {
      label: t("journals"),
      to: `/${currentLang}/app/journals`,
      icon: <MenuBookIcon fontSize="small" />,
      active: isRouteActive("/app/journals"),
      moduleKey: "accounting"
    }, {
      label: t("reports"),
      to: `/${currentLang}/app/accounting-reports`,
      icon: <AssessmentIcon fontSize="small" />,
      active: isRouteActive("/app/accounting-reports"),
      moduleKey: "accounting"
    }, {
      label: t("assets"),
      to: `/${currentLang}/app/assets`,
      icon: <BusinessIcon fontSize="small" />,
      active: isRouteActive("/app/assets"),
      moduleKey: "assets"
    }, {
      label: t("projects"),
      to: `/${currentLang}/app/projects`,
      icon: <WorkIcon fontSize="small" />,
      active: isRouteActive("/app/projects"),
      moduleKey: "projects"
    }, {
      label: t("profit_centers"),
      to: `/${currentLang}/app/profit-centers`,
      icon: <TrendingUpIcon fontSize="small" />,
      active: isRouteActive("/app/profit-centers"),
      moduleKey: "costCenters"
    }, {
      label: t("cost_centers"),
      to: `/${currentLang}/app/costcenters`,
      icon: <AccountBalanceWalletIcon fontSize="small" />,
      active: isRouteActive("/app/costcenters"),
      moduleKey: "costCenters"
    }, {
      label: t("vouchers"),
      to: `/${currentLang}/app/vouchers`,
      icon: <LocalOfferIcon fontSize="small" />,
      active: isRouteActive("/app/vouchers"),
      moduleKey: "accounting"
    }, {
      label: t("credit_notes"),
      to: `/${currentLang}/app/credit-debit-notes?type=credit`,
      icon: <ArrowDownwardIcon fontSize="small" />,
      active: isRouteActive("/app/credit-debit-notes"),
      moduleKey: "accounting"
    }, {
      label: t("debit_notes"),
      to: `/${currentLang}/app/credit-debit-notes?type=debit`,
      icon: <ArrowUpwardIcon fontSize="small" />,
      active: false,
      moduleKey: "accounting"
    }]
  }, {
    key: "inventory",
    label: t("inventory"),
    icon: <InventoryIcon fontSize="small" />,
    moduleKey: "inventory",
    items: [{
      label: t("inventory_home") || "Inventory Home",
      to: `/${currentLang}/app/inventory/home`,
      icon: <InventoryIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/home")
    }, {
      label: t("inventory_items"),
      to: `/${currentLang}/app/inventory/items`,
      icon: <InventoryIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/items")
    }, {
      label: t("products"),
      to: `/${currentLang}/app/inventory/products`,
      icon: <CategoryIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/products") && !isRouteActive("/app/inventory/products/categories")
    }, {
      label: t("products_categories"),
      to: `/${currentLang}/app/inventory/products/categories`,
      icon: <CategoryIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/products/categories")
    }, {
      label: t("warehouses"),
      to: `/${currentLang}/app/inventory/warehouses`,
      icon: <WarehouseIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/warehouses")
    }, {
      label: t("units_of_measure"),
      to: `/${currentLang}/app/inventory/units`,
      icon: <StraightenIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/units")
    }, {
      label: t("inventory_adjustments"),
      to: `/${currentLang}/app/inventory/adjustments`,
      icon: <TuneIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/adjustments")
    }, {
      label: t("inventory_transactions"),
      to: `/${currentLang}/app/inventory/transactions`,
      icon: <SyncAltIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/transactions")
    }, {
      label: t("stock_transfers"),
      to: `/${currentLang}/app/inventory/transfers`,
      icon: <LocalShippingIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/transfers")
    }, {
      label: t("low_stock_alerts"),
      to: `/${currentLang}/app/inventory/alerts`,
      icon: <WarningIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/alerts")
    }, {
      label: t("inventory_reports"),
      to: `/${currentLang}/app/inventory/reports`,
      icon: <AssessmentIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/reports")
    }, {
      label: t("inventory_search"),
      to: `/${currentLang}/app/inventory/search`,
      icon: <SearchIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/search")
    }, {
      label: t("inventory_orders"),
      to: `/${currentLang}/app/inventory-orders`,
      icon: <ListAltIcon fontSize="small" />,
      active: isRouteActive("/app/inventory-orders")
    }, {
      label: t("inventory_settings"),
      to: `/${currentLang}/app/inventory/settings`,
      icon: <SettingsIcon fontSize="small" />,
      active: isRouteActive("/app/inventory/settings")
    }]
  }, {
    key: "modules",
    label: t("Business Modules"),
    icon: <BusinessIcon fontSize="small" />,
    items: [{
      label: t("construction"),
      to: `/${currentLang}/app/construction/projects`,
      icon: <ConstructionIcon fontSize="small" />,
      active: isRouteActive("/app/construction"),
      moduleKey: "construction"
    }, {
      label: t("manufacturing"),
      to: `/${currentLang}/app/manufacturing`,
      icon: <PrecisionManufacturingIcon fontSize="small" />,
      active: isRouteActive("/app/manufacturing"),
      moduleKey: "manufacturing"
    }, {
      label: t("law_firm"),
      to: `/${currentLang}/app/law-firm`,
      icon: <GavelIcon fontSize="small" />,
      active: isRouteActive("/app/law-firm"),
      moduleKey: "lawFirm"
    }, {
      label: t("clinic"),
      to: `/${currentLang}/app/clinic`,
      icon: <MedicalServicesIcon fontSize="small" />,
      active: isRouteActive("/app/clinic"),
      moduleKey: "clinic"
    }, {
      label: t("rentals"),
      to: `/${currentLang}/app/rentals/home`,
      icon: <ApartmentIcon fontSize="small" />,
      active: isRouteActive("/app/rentals"),
      moduleKey: "rentals"
    }, {
      label: t("properties"),
      to: `/${currentLang}/app/properties`,
      icon: <HomeWorkIcon fontSize="small" />,
      active: isRouteActive("/app/properties"),
      moduleKey: "properties"
    }]
  }, {
    key: "management",
    label: t("Management"),
    icon: <WorkIcon fontSize="small" />,
    items: [{
      label: t("attendance"),
      to: `/${currentLang}/app/attendance/home`,
      icon: <AccessTimeIcon fontSize="small" />,
      active: isRouteActive("/app/attendance"),
      moduleKey: "attendance"
    }, {
      label: t("payrolls"),
      to: `/${currentLang}/app/payrolls`,
      icon: <PaymentsIcon fontSize="small" />,
      active: isRouteActive("/app/payrolls"),
      moduleKey: "payrolls"
    }, {
      label: t("letters"),
      to: `/${currentLang}/app/letters/home`,
      icon: <MailIcon fontSize="small" />,
      active: isRouteActive("/app/letters"),
      moduleKey: "letters"
    }, {
      label: t("excel_workbooks"),
      to: `/${currentLang}/app/excel-workbooks`,
      icon: <TableChartIcon fontSize="small" />,
      active: isRouteActive("/app/excel-workbooks"),
      moduleKey: "excel"
    }, {
      label: t("import/export"),
      to: `/${currentLang}/app/excel`,
      icon: <ImportExportIcon fontSize="small" />,
      active: isRouteActive("/app/excel") && !isRouteActive("/app/excel-workbooks"),
      moduleKey: "importExport"
    }, {
      label: t("my_website"),
      to: `/${currentLang}/app/website`,
      icon: <LanguageIcon fontSize="small" />,
      active: isRouteActive("/app/website"),
      moduleKey: "website"
    }]
  }];

  // Filter each group's items by module visibility; drop the whole group if nothing is left
  const menuGroups = allMenuGroups.map(group => {
    // Groups with a top-level moduleKey gate all items at once (e.g. inventory)
    if (group.moduleKey && !modules[group.moduleKey]) return null;
    const visibleItems = group.items.filter(item => !item.moduleKey || modules[item.moduleKey]);
    if (visibleItems.length === 0) return null;
    return {
      ...group,
      items: visibleItems
    };
  }).filter(Boolean);
  const renderDropdown = groupKey => {
    const group = menuGroups.find(g => g.key === groupKey);
    if (!group) return null;
    const isGroupActive = group.items.some(item => item.active);
    return <div key={group.key} className="tnl-dropdown">
        <button className={`tnl tnl-btn ${isGroupActive ? "tnl-active" : ""} ${openDropdown === group.key ? "tnl-open" : ""}`} onClick={() => setOpenDropdown(openDropdown === group.key ? null : group.key)}>
          {group.icon}
          <span>{group.label}</span>
        </button>
        {openDropdown === group.key && <div className="tnl-dropdown-menu" style={{
        maxHeight: "70vh",
        overflowY: "auto"
      }}>
            {group.items.map(item => <Link key={item.to} to={item.to} className={`tnl-dropdown-item ${item.active ? "tnl-dropdown-item-active" : ""}`}>
                {item.icon}
                <span>{item.label}</span>
              </Link>)}
          </div>}
      </div>;
  };
  return <nav ref={navRef} className="top-nav np">
      {/* Desktop navigation */}
      <div className="top-nav-inner">
        {/* Logo */}
        <Link to={`/${currentLang}/app/main`} className="top-nav-logo">
          <img src="/logo.svg" alt={t("logo")} style={{
          height: "28px"
        }} />
        </Link>

        {/* Direct links and dropdowns */}
        <div className="top-nav-links">
          <Link to={`/${currentLang}/app/main`} className={`tnl ${isRouteActive("/app/main") ? "tnl-active" : ""}`}>
            <HomeIcon fontSize="small" /><span>{t("home")}</span>
          </Link>

          {renderDropdown("accounting")}

          {modules.customers && <Link to={`/${currentLang}/app/customers`} className={`tnl ${isRouteActive("/app/customers") ? "tnl-active" : ""}`}>
              <PeopleIcon fontSize="small" /><span>{t("customers")}</span>
            </Link>}

          {renderDropdown("inventory")}
          {renderDropdown("modules")}
          {renderDropdown("management")}

          {modules.todos && <Link to={`/${currentLang}/app/todos`} className={`tnl ${isRouteActive("/app/todos") ? "tnl-active" : ""}`}>
              <ChecklistIcon fontSize="small" /><span>{t("todo_lists")}</span>
            </Link>}

          {modules.dashboard && <Link to={`/${currentLang}/app/dashboard`} className={`tnl ${isRouteActive("/app/dashboard") ? "tnl-active" : ""}`}>
              <DashboardIcon fontSize="small" /><span>{t("dashboard")}</span>
            </Link>}
        </div>

        {/* Action icons — right side */}
        <div className="top-nav-actions">
          <Tooltip title={t("back") || "Back"}>
            <IconButton size="small" className="tna-btn" onClick={() => history.goBack()}>
              <ChevronLeft size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("forward") || "Forward"}>
            <IconButton size="small" className="tna-btn" onClick={() => history.goForward()}>
              <ChevronRight size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("reload") || "Reload"}>
            <IconButton size="small" className="tna-btn" onClick={() => window.location.reload()}>
              <RotateCcw size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Change Language")}>
            <IconButton size="small" className="tna-btn" onClick={e => setLanguageAnchorEl(e.currentTarget)}>
              <LanguageIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Messages")}>
            <IconButton size="small" className="tna-btn" onClick={() => {
            dispatch(fetchUnreadCount());
            openLink(`/${currentLang}/app/messaging`);
          }}>
              <Badge badgeContent={unreadMessageCount || null} color="error">
                <Bell size={18} />
              </Badge>
            </IconButton>
          </Tooltip>

          {modules.chat && <Tooltip title={t("Messages")}>
              <IconButton size="small" className="tna-btn" onClick={() => {
            dispatch(fetchUnreadCount());
            openLink(`/${currentLang}/app/messaging`);
          }}>
                <Badge badgeContent={unreadMessageCount} color="error">
                  <MessageCircle size={18} style={{
                color: "#a8d8a8"
              }} />
                </Badge>
              </IconButton>
            </Tooltip>}

          <Tooltip title={t("Settings")}>
            <IconButton size="small" className="tna-btn" onClick={() => openLink(`/${currentLang}/app/settings`)}>
              <Settings size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Help")}>
            <IconButton size="small" className="tna-btn">
              <HelpCircle size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Toggle Fullscreen")}>
            <IconButton size="small" className="tna-btn" onClick={toggleFullScreen}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"}>
            <IconButton size="small" className="tna-btn" onClick={toggleDarkMode}>
              {isDarkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Account")}>
            <IconButton size="small" className="tna-btn" onClick={e => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{
              width: 28,
              height: 28,
              bgcolor: "rgba(255,255,255,0.25)",
              fontSize: "0.8rem"
            }}>
                {user?.result?.name?.charAt(0)}
              </Avatar>
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Mobile hamburger button */}
      <button className="top-nav-mobile-btn" onClick={() => setMobileOpen(!mobileOpen)} aria-label={t("toggle mobile menu")}>
        {mobileOpen ? <CloseIcon /> : <MenuIcon />}
      </button>

      {/* Mobile menu panel */}
      {mobileOpen && <div className="top-nav-mobile-menu">
          <Link to={`/${currentLang}/app/main`} className={`mnl ${isRouteActive("/app/main") ? "mnl-active" : ""}`}>
            <HomeIcon fontSize="small" />
            <span>{t("home")}</span>
          </Link>

          {menuGroups.map(group => <div key={group.key} className="mnl-group">
              <button className={`mnl-group-header ${mobileExpanded === group.key ? "mnl-open" : ""}`} onClick={() => setMobileExpanded(mobileExpanded === group.key ? null : group.key)}>
                <span className="mnl-group-label">
                  {group.icon}
                  {group.label}
                </span>
              </button>
              {mobileExpanded === group.key && group.items.map(item => <Link key={item.to} to={item.to} className={`mnl mnl-sub ${item.active ? "mnl-active" : ""}`}>
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>)}
            </div>)}

          {modules.customers && <Link to={`/${currentLang}/app/customers`} className={`mnl ${isRouteActive("/app/customers") ? "mnl-active" : ""}`}>
              <PeopleIcon fontSize="small" /><span>{t("customers")}</span>
            </Link>}

          {modules.todos && <Link to={`/${currentLang}/app/todos`} className={`mnl ${isRouteActive("/app/todos") ? "mnl-active" : ""}`}>
              <ChecklistIcon fontSize="small" /><span>{t("todo_lists")}</span>
            </Link>}

          {modules.dashboard && <Link to={`/${currentLang}/app/dashboard`} className={`mnl ${isRouteActive("/app/dashboard") ? "mnl-active" : ""}`}>
              <DashboardIcon fontSize="small" /><span>{t("dashboard")}</span>
            </Link>}

          <button className="mnl" style={{
        width: "100%",
        textAlign: "start"
      }} onClick={logout}>
            <LogOut size={16} />
            <span>{t("logout")}</span>
          </button>
        </div>}

      {/* Profile Menu */}
      <Menu anchorEl={anchorEl} id="account-menu" open={profileOpen} onClose={() => setAnchorEl(null)} onClick={() => setAnchorEl(null)} PaperProps={{
      elevation: 0,
      sx: {
        overflow: "visible",
        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
        mt: 1.5,
        width: 200
      }
    }} transformOrigin={{
      horizontal: "right",
      vertical: "top"
    }} anchorOrigin={{
      horizontal: "right",
      vertical: "bottom"
    }}>
        <MenuItem onClick={() => openLink(`/${currentLang}/app/profile`)}>
          <ListItemIcon><User size={16} /></ListItemIcon>
          {t("profile")}
        </MenuItem>
        <MenuItem onClick={() => openLink(`/${currentLang}/app/settings`)}>
          <ListItemIcon><Settings size={16} /></ListItemIcon>
          {t("settings")}
        </MenuItem>
        <Divider />
        <MenuItem onClick={logout}>
          <ListItemIcon><LogOut size={16} /></ListItemIcon>
          {t("logout")}
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu anchorEl={notificationAnchorEl} id="notification-menu" open={notificationOpen} onClose={() => setNotificationAnchorEl(null)} onClick={() => setNotificationAnchorEl(null)} PaperProps={{
      elevation: 0,
      sx: {
        overflow: "visible",
        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
        mt: 1.5,
        width: 300
      }
    }} transformOrigin={{
      horizontal: "right",
      vertical: "top"
    }} anchorOrigin={{
      horizontal: "right",
      vertical: "bottom"
    }}>
        <MenuItem disabled>
          <span style={{
          fontSize: "0.85rem",
          color: "#666"
        }}>{t("no_new_notifications") || "No new notifications"}</span>
        </MenuItem>
      </Menu>

      {/* Language Menu */}
      <Menu anchorEl={languageAnchorEl} id="language-menu" open={languageOpen} onClose={() => setLanguageAnchorEl(null)} PaperProps={{
      elevation: 0,
      sx: {
        overflowX: "hidden",
        overflowY: "auto",
        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
        mt: 1.5,
        width: "max-content",
        minWidth: 190,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "min(70vh, 520px)"
      }
    }} transformOrigin={{
      horizontal: "right",
      vertical: "top"
    }} anchorOrigin={{
      horizontal: "right",
      vertical: "bottom"
    }}>
        {Object.keys(lngs).map(lng => <MenuItem key={lng} onClick={() => handleLanguageChange(lng)} selected={currentLang === lng} sx={{
        fontSize: "0.85rem",
        minHeight: 40,
        whiteSpace: "nowrap"
      }}>
            {lngs[lng].nativeName}
          </MenuItem>)}
      </Menu>
    </nav>;
};
export default TopNav;
